import React, { useState, useEffect } from 'react';

interface VolumeDetailProps {
  brand: string;
  volume: {
    slug: string;
    title: string;
    lifecycleState: string;
    chapterList: { slug: string; title?: string; order: number }[];
  };
  onBack: () => void;
  onAction: (action: string, slug: string) => void;
  onSelectChapter: (slug: string) => void;
}

interface RenditionStatus {
  html: boolean; pdf: boolean; epub: boolean;
  quartoHtml: boolean; quartoPdf: boolean; quartoEpub: boolean;
  experience: boolean; report: boolean; designPdf: boolean; md: boolean;
}

const EMPTY_RENDITIONS: RenditionStatus = {
  html: false, pdf: false, epub: false,
  quartoHtml: false, quartoPdf: false, quartoEpub: false,
  experience: false, report: false, designPdf: false, md: false,
};

function humanTitle(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const STATUS_LABELS: Record<string, string> = {
  spark: 'Idea', research: 'Researching', plan: 'Planning', draft: 'Writing',
  review: 'Review', ready: 'Ready', published: 'Published',
};

export default function VolumeDetail({ brand, volume, onBack, onAction, onSelectChapter }: VolumeDetailProps) {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [renditions, setRenditions] = useState<RenditionStatus>(EMPTY_RENDITIONS);

  const [genStatus, setGenStatus] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const [ebookBuilding, setEbookBuilding] = useState(false);
  const [ebookStatus, setEbookStatus] = useState<string | null>(null);

  const [epubGenerating, setEpubGenerating] = useState(false);
  const [quartoBuilding, setQuartoBuilding] = useState(false);
  const [quartoStatus, setQuartoStatus] = useState<string | null>(null);

  const refreshRenditions = () =>
    fetch(`/api/${brand}/volumes/${volume.slug}/renditions`).then(r => r.json()).then(setRenditions).catch(() => {});

  const refreshDetail = () => {
    setLoading(true);
    fetch(`/api/${brand}/volumes/${volume.slug}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { setDetail(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    setDetail(null);
    refreshDetail();
    refreshRenditions();
  }, [volume.slug, brand]);

  // Live status: ONLY while a generation is in flight, poll chapter statuses
  // every 6s so the UI updates on its own — no manual Refresh needed. Gated on
  // `generating` so it never runs on an idle volume; self-stops (and clears the
  // interval) once every chapter is written or a ~5min ceiling is hit. Uses a
  // direct fetch (not refreshDetail) to avoid the loading flicker. This is the
  // single live poller — handleGenerateAll just flips `generating` on.
  useEffect(() => {
    if (!generating) return;
    let stop = false;
    let ticks = 0;
    let id: ReturnType<typeof setInterval>;
    const finish = () => { stop = true; clearInterval(id); setGenerating(false); };
    const tick = async () => {
      if (stop) return;
      ticks++;
      try {
        const r = await fetch(`/api/${brand}/volumes/${volume.slug}`);
        // Volume gone from the backend (e.g. workspace reset after a redeploy):
        // stop the spinner immediately and surface it, instead of polling a
        // missing volume until the 5-minute ceiling. Gate on the HTTP status
        // (like refreshDetail does) — a 200 body carrying an `error` field is
        // not a missing volume.
        if (!r.ok) {
          const e = await r.json().catch(() => ({} as { error?: string }));
          setGenStatus(`This collection is no longer on the server (${e.error || `HTTP ${r.status}`}). Reload the library and re-create it.`);
          refreshRenditions();
          finish();
          return;
        }
        const d = await r.json();
        setDetail(d);
        const cd = (d && d.chapterDetails) || {};
        const slugs = Object.keys(cd);
        const written = slugs.filter((s) => {
          const st = cd[s] && cd[s].lifecycleState;
          return st && !['spark', 'research', 'plan'].includes(st);
        });
        if (slugs.length > 0 && written.length === slugs.length) { finish(); return; }
      } catch { /* keep polling */ }
      if (ticks >= 50) { finish(); return; } // safety ceiling (~5min) for a stuck run
      refreshRenditions();
    };
    id = setInterval(tick, 6000);
    return () => { stop = true; clearInterval(id); };
  }, [volume.slug, brand, generating]);

  // STEP 1 — generate every chapter in one pass via the OpenRouter volume pipeline.
  const handleGenerateAll = () => {
    onAction('pipeline', volume.slug);
    setGenerating(true); // the `generating`-gated effect above drives live status polling
    setGenStatus('Generating all chapters in the background. Each chapter is researched and written in one pass — no chapter-by-chapter queueing. Chapter statuses below update as the pipeline progresses.');
  };

  // STEP 2 — render the #179 design ebook (experience + report + PDF + md).
  const handleGenerateEbook = async () => {
    setEbookBuilding(true);
    setEbookStatus('Rendering the ebook: interactive web experience + print PDF + Markdown. This runs headless Chrome for the PDF and can take ~30s...');
    try {
      const res = await fetch(`/api/${brand}/volumes/${volume.slug}/render-ebook`, { method: 'POST' });
      if (!res.ok) { const e = await res.json(); setEbookStatus(`Error: ${e.error || 'Render failed'}`); setEbookBuilding(false); return; }
      const poll = setInterval(async () => {
        const data = await fetch(`/api/${brand}/volumes/${volume.slug}/renditions`).then(r => r.json()).catch(() => null);
        if (data && data.experience) {
          clearInterval(poll);
          setRenditions(data);
          setEbookBuilding(false);
          setEbookStatus(data.designPdf ? 'Ebook ready — web experience + PDF + Markdown.' : 'Ebook ready — web experience + Markdown (PDF needs Chrome on the server).');
          setTimeout(() => setEbookStatus(null), 6000);
        }
      }, 4000);
      setTimeout(() => { clearInterval(poll); if (ebookBuilding) { setEbookBuilding(false); setEbookStatus('Timed out — check server logs.'); } }, 180000);
    } catch { setEbookStatus('Render request failed'); setEbookBuilding(false); }
  };

  const handlePreview = () => window.open(`/api/${brand}/volumes/${volume.slug}/preview`, '_blank');
  const handleDownloadExperience = () => window.open(`/api/${brand}/volumes/${volume.slug}/download/experience`, '_blank');
  const handleDownloadMd = () => window.open(`/api/${brand}/volumes/${volume.slug}/download/md`, '_blank');
  const handleDownloadPdf = () => window.open(`/api/${brand}/volumes/${volume.slug}/download/pdf`, '_blank');

  const handleBuildQuarto = async () => {
    setQuartoBuilding(true);
    setQuartoStatus('Building EPUB with Quarto (this may take 1-2 minutes)...');
    try {
      const res = await fetch(`/api/${brand}/volumes/${volume.slug}/render-quarto`, { method: 'POST' });
      if (!res.ok) { const e = await res.json(); setQuartoStatus(`Error: ${e.error || 'Build failed'}`); setQuartoBuilding(false); return; }
      const poll = setInterval(async () => {
        const data = await fetch(`/api/${brand}/volumes/${volume.slug}/renditions`).then(r => r.json()).catch(() => null);
        if (data && (data.quartoHtml || data.quartoPdf || data.quartoEpub)) {
          clearInterval(poll);
          setRenditions(data);
          setQuartoBuilding(false);
          setQuartoStatus('EPUB build complete.');
          setTimeout(() => setQuartoStatus(null), 5000);
        }
      }, 5000);
      setTimeout(() => { clearInterval(poll); if (quartoBuilding) { setQuartoBuilding(false); setQuartoStatus('Build timed out. Check server logs.'); } }, 300000);
    } catch { setQuartoStatus('Build request failed'); setQuartoBuilding(false); }
  };

  const handleDownloadEpub = async () => {
    setEpubGenerating(true);
    try {
      const res = await fetch(`/api/${brand}/volumes/${volume.slug}/download/epub`);
      if (!res.ok) { const e = await res.json(); alert(e.error || 'EPUB generation failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${volume.slug}.epub`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('EPUB generation failed'); }
    finally { setEpubGenerating(false); }
  };

  const chapters = volume.chapterList.sort((a, b) => a.order - b.order);
  const chapterDetails: Record<string, any> = detail?.chapterDetails || {};
  const writtenCount = chapters.filter((ch) => {
    const st = chapterDetails[ch.slug]?.lifecycleState;
    return st && !['spark', 'research', 'plan'].includes(st);
  }).length;
  const allWritten = writtenCount === chapters.length && chapters.length > 0;

  const pillBtn = (bg: string): React.CSSProperties => ({
    background: bg, color: 'var(--paper)', border: 'none', borderRadius: 0,
    padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 'var(--fs-small)',
  });

  return (
    <div className="piece-detail">
      <div className="piece-detail__inner">
        <div className="piece-detail__topbar">
          <button className="piece-detail__back" onClick={onBack}>{'←'} Library</button>
        </div>

        <h1 className="apple-view__title">{volume.title || humanTitle(volume.slug)}</h1>
        <div className="apple-view__meta">
          <span className={`status-pill status-pill--${['spark','research','plan'].includes(volume.lifecycleState) ? 'idea' : volume.lifecycleState}`}>
            {STATUS_LABELS[volume.lifecycleState] || volume.lifecycleState}
          </span>
          <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--fs-small)' }}>
            {chapters.length} chapter{chapters.length !== 1 ? 's' : ''}{chapters.length ? ` · ${writtenCount}/${chapters.length} written` : ''}
          </span>
        </div>

        {loading && !detail ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading volume…</div>
        ) : (
          <>
            {/* Chapter list */}
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 'var(--fs-eyebrow)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', marginBottom: 12 }}>
                Chapters
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {chapters.map((ch, i) => {
                  const chDetail = chapterDetails[ch.slug];
                  const state = chDetail?.lifecycleState || 'spark';
                  return (
                    <button
                      key={ch.slug}
                      className="library__row"
                      onClick={() => onSelectChapter(ch.slug)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 0, cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: 'var(--font)' }}
                    >
                      <span style={{ fontSize: 'var(--fs-meta)', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums', minWidth: 24 }}>{i + 1}.</span>
                      <span style={{ flex: 1, fontSize: 'var(--fs-body)', color: 'var(--text)' }}>{ch.title || humanTitle(ch.slug)}</span>
                      <span className={`status-pill status-pill--${['spark','research','plan'].includes(state) ? 'idea' : state === 'draft' ? 'writing' : state}`} style={{ fontSize: 'var(--fs-eyebrow)' }}>
                        {STATUS_LABELS[state] || state}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* STEP 1: Generate all chapters via OpenRouter (one pass) */}
            <div style={{ marginTop: 24, padding: 20, border: '2px solid var(--zop-orange)', borderRadius: 0, background: 'var(--bg-secondary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ background: 'var(--zop-orange)', color: 'var(--paper)', borderRadius: 0, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-eyebrow)', fontWeight: 700, flexShrink: 0 }}>1</span>
                <div style={{ fontSize: 'var(--fs-small)', fontWeight: 700, color: 'var(--text)' }}>Generate All Chapters</div>
              </div>
              <div style={{ fontSize: 'var(--fs-meta)', color: 'var(--text-secondary)', marginBottom: 12, paddingLeft: 28 }}>
                Researches the topic and writes <strong>every chapter in one pass</strong> through the generation pipeline. No chapter-by-chapter queueing. Runs in the background — chapter statuses above update as each chapter is composed.
              </div>
              {genStatus && (
                <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 0, background: generating ? 'var(--warning-light)' : genStatus.startsWith('Error') ? 'var(--danger-light)' : 'var(--success-light)', color: generating ? 'var(--zop-orange)' : 'var(--text)', fontSize: 'var(--fs-small)' }}>
                  {genStatus}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={handleGenerateAll} disabled={generating} style={pillBtn(generating ? 'var(--g-300)' : 'var(--zop-orange)')}>
                  {generating ? 'Generating…' : allWritten ? 'Regenerate All Chapters' : `Generate ${chapters.length} Chapter${chapters.length !== 1 ? 's' : ''}`}
                </button>
                <button onClick={() => { refreshDetail(); refreshRenditions(); }} style={{ ...pillBtn('var(--bg)'), color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                  Refresh
                </button>
              </div>
            </div>

            {/* STEP 2: Generate the ebook (design experience + PDF + MD) */}
            <div style={{ marginTop: 12, padding: 20, border: '2px solid var(--zop-blue)', borderRadius: 0, background: 'var(--bg-secondary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ background: 'var(--zop-blue)', color: 'var(--paper)', borderRadius: 0, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-eyebrow)', fontWeight: 700, flexShrink: 0 }}>2</span>
                <div style={{ fontSize: 'var(--fs-small)', fontWeight: 700, color: 'var(--text)' }}>Generate the Ebook (design)</div>
              </div>
              <div style={{ fontSize: 'var(--fs-meta)', color: 'var(--text-secondary)', marginBottom: 12, paddingLeft: 28 }}>
                Compiles all chapters into the <strong>interactive web experience</strong> (scroll-read with chapter dividers, stat call-outs, figures, bookmark + highlight) plus a <strong>print-ready PDF</strong> and a clean <strong>Markdown</strong>. Run after the chapters are written.
              </div>
              {ebookStatus && (
                <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 0, background: ebookBuilding ? 'var(--warning-light)' : ebookStatus.startsWith('Error') ? 'var(--danger-light)' : 'var(--success-light)', color: ebookBuilding ? 'var(--zop-orange)' : ebookStatus.startsWith('Error') ? 'var(--zop-orange)' : 'var(--success)', fontSize: 'var(--fs-small)' }}>
                  {ebookStatus}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={handleGenerateEbook} disabled={ebookBuilding} style={pillBtn(ebookBuilding ? 'var(--g-300)' : 'var(--zop-blue)')}>
                  {ebookBuilding ? 'Generating…' : renditions.experience ? 'Regenerate Ebook' : 'Generate Ebook'}
                </button>
                {renditions.experience && (
                  <button onClick={handlePreview} style={pillBtn('var(--accent)')}>Preview</button>
                )}
                {renditions.designPdf && (
                  <button onClick={handleDownloadPdf} style={pillBtn('var(--zop-orange)')}>Download PDF</button>
                )}
                {renditions.experience && (
                  <button onClick={handleDownloadExperience} style={{ ...pillBtn('var(--bg)'), color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>Download HTML</button>
                )}
                {renditions.md && (
                  <button onClick={handleDownloadMd} style={{ ...pillBtn('var(--bg)'), color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>Download Markdown</button>
                )}
              </div>
              {renditions.experience && (
                <div style={{ marginTop: 8, fontSize: 'var(--fs-eyebrow)', color: 'var(--text-tertiary)' }}>
                  Ready: web experience{renditions.designPdf ? ' · PDF' : ''}{renditions.md ? ' · Markdown' : ''}
                </div>
              )}
            </div>

            {/* Advanced: EPUB via Quarto */}
            <div style={{ marginTop: 12, padding: 16, border: '1px solid var(--border)', borderRadius: 0, background: 'var(--bg-secondary)' }}>
              <div style={{ fontSize: 'var(--fs-eyebrow)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', marginBottom: 6 }}>Advanced · EPUB</div>
              <div style={{ fontSize: 'var(--fs-meta)', color: 'var(--text-secondary)', marginBottom: 10 }}>
                Build an <strong>EPUB</strong> (e-reader format) with Quarto. The web experience + PDF above are the primary outputs; use this only if you need EPUB.
              </div>
              {quartoStatus && (
                <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 0, background: quartoBuilding ? 'var(--warning-light)' : quartoStatus.startsWith('Error') ? 'var(--danger-light)' : 'var(--success-light)', color: quartoBuilding ? 'var(--zop-orange)' : 'var(--text)', fontSize: 'var(--fs-small)' }}>
                  {quartoStatus}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" style={{ fontSize: 'var(--fs-meta)' }} onClick={handleBuildQuarto} disabled={quartoBuilding}>
                  {quartoBuilding ? 'Building…' : 'Build EPUB (Quarto)'}
                </button>
                {renditions.quartoEpub && (
                  <button onClick={handleDownloadEpub} disabled={epubGenerating} style={pillBtn('var(--success)')}>
                    {epubGenerating ? 'Downloading…' : 'Download EPUB'}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
